import { IDetailsRowProps, MessageBarType } from '@fluentui/react';
import {
  debounce,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  EmptyMessage,
  getItemsWithEmptyItemsOfNextPage,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  ListScheduledTasksRequest,
  ListScheduledTasksResponse,
  ScheduledTask,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import {
  IScheduledTaskListClassNames,
  IScheduledTaskListStyles,
  linkStylesIconOnly,
  scheduledTaskListStylesDefault,
} from './ScheduledTaskList.styles';

const getColumns = (
  classNames: IScheduledTaskListClassNames,
  onEdit: (item: ScheduledTask) => void,
  onDelete: (item: ScheduledTask) => void,
  onPauseOrResume: (item: ScheduledTask, paused?: boolean) => void,
  canEdit?: boolean
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'description',
      minWidth: 200,
      data: {
        headerFieldName: 'name',
        listCellProps: { emptyMessage: 'No Description', iconProps: { iconName: 'FormLibraryMirrored' } },
      },
    },
    {
      key: 'schedule',
      name: 'Schedule',
      fieldName: 'schedule',
      minWidth: 150,
    },
    {
      key: 'nextRunTime',
      name: 'Next Run Time',
      fieldName: 'nextRunTime',
      minWidth: 110,
      data: {
        listCellProps: { emptyMessage: '-' },
      },
    },
    {
      key: 'lastExec',
      name: 'Last Execution Time',
      fieldName: 'lastExec',
      minWidth: 110,
      data: {
        listCellProps: { emptyMessage: '-' },
      },
    },
    {
      key: 'owner',
      name: 'Owner',
      fieldName: 'ownerName',
      minWidth: 120,
    },
    {
      key: 'createdDateTime',
      name: 'Created Date Time',
      fieldName: 'createdDateTime',
      minWidth: 110,
    },
    {
      key: 'taskType',
      name: 'Task Type',
      fieldName: 'taskTypeName',
      minWidth: 100,
    },
    {
      key: 'action',
      name: 'Action',
      minWidth: 160,
      onRender: (item: ScheduledTask) => {
        return (
          <div className={classNames.actions}>
            <div className={classNames.actionButtons}>
              {canEdit && (
                <>
                  <Link
                    title="Edit"
                    styles={[linkStylesGhost, linkStylesIconOnly]}
                    data-test="edit-scheduled-task-button"
                    iconName="Edit"
                    onClick={() => onEdit(item)}
                  ></Link>
                  <Link
                    title="Delete"
                    styles={[linkStylesGhost, linkStylesIconOnly]}
                    data-test="delete-scheduled-task-button"
                    iconName="Delete"
                    onClick={() => onDelete(item)}
                  ></Link>
                  <Link
                    title={item.nextRunTime ? 'Pause' : 'Resume'}
                    styles={[linkStylesGhost, linkStylesIconOnly]}
                    data-test={item.nextRunTime ? 'pause-scheduled-task-button' : 'resume-scheduled-task-button'}
                    iconName={item.nextRunTime ? 'Pause' : 'PlayResume'}
                    onClick={() => {
                      onPauseOrResume(item, !item.nextRunTime);
                    }}
                  ></Link>
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

interface ScheduledTaskWithInfo extends ScheduledTask {
  ownerName?: string;
  taskTypeName?: string;
}

export interface ScheduledTaskListData {
  items: ScheduledTaskWithInfo[];
  pageToken?: string;
  pageSize?: number;
}

const convertScheduledTaskWithInfo = (item: ScheduledTask): ScheduledTaskWithInfo => {
  const taskTimeName = item.taskType?.toLowerCase().replace('_', ' ');
  return {
    ...item,
    ownerName: item.owner?.name,
    taskTypeName: taskTimeName ? taskTimeName.charAt(0).toUpperCase() + taskTimeName.slice(1) : '',
  };
};

interface IScheduledTaskListProps {
  reload?: boolean;
  featureSetId?: string;
  canEdit?: boolean;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onEdit: (task: ScheduledTask) => void;
}

export const ScheduledTaskList = ({ featureSetId, reload, canEdit, onUpdateData, onEdit }: IScheduledTaskListProps) => {
  const [data, setData] = useState<ScheduledTaskListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refMeta = useRef<{
    isLoadingPage: boolean;
    mounted?: boolean;
  }>({
    isLoadingPage: false,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: ScheduledTaskListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading scheduledTasks...`);
      try {
        const pageSize = defaultPageSize;
        const req: ListScheduledTasksRequest = {
          pageSize,
          pageToken,
          featureSetId,
        };
        const { tasks = [], nextPageToken = '' }: ListScheduledTasksResponse = await fsApi(
          CoreService.ListScheduledTasks,
          req
        );
        const newItems = getItemsWithEmptyItemsOfNextPage(items, tasks, pageSize, convertScheduledTaskWithInfo);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        if (!refMeta.current.mounted) return;
        setData({
          ...newData,
        });
        onUpdateData &&
          onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.scheduledTasks);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading scheduled tasks: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, onUpdateData]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onDelete = useCallback(
    async (item: ScheduledTask) => {
      const { id, name } = item;
      try {
        await fsApi(CoreService.DeleteScheduledTask, { scheduledTaskId: id });
        addToast({
          messageBarType: MessageBarType.success,
          message: `Scheduled "${name}" has been deleted successfully!`,
        });
        load();
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting Scheduled Task: `));
      }
    },
    [load]
  );
  const onPauseOrResume = useCallback(
    async (item: ScheduledTask, paused?: boolean) => {
      const { id, name } = item;
      try {
        if (paused) {
          await fsApi(CoreService.ResumeScheduledTask, { scheduledTaskId: id });
        } else {
          await fsApi(CoreService.PauseScheduledTask, { scheduledTaskId: id });
        }
        addToast({
          messageBarType: MessageBarType.success,
          message: `Scheduled "${name}" has been ${paused ? 'resumed' : 'paused'} successfully!`,
        });
        load();
      } catch (err) {
        addToast(
          getErrorMessageBarMessage(
            err,
            `An error has occurred while ${paused ? 'resuming' : 'pausing'} Scheduled Task: `
          )
        );
      }
    },
    [load]
  );
  const classNames = useClassNames<IScheduledTaskListStyles, IScheduledTaskListClassNames>(
    'ScheduledTaskList',
    scheduledTaskListStylesDefault
  );
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    isHeaderVisible: false,
    columns: getColumns(classNames, onEdit, onDelete, onPauseOrResume, canEdit),
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return <ListRow data={item} columns={columns} loading={item.isNotLoaded} />;
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  return (
    <div className={classNames.root} data-test="scheduledTask-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="scheduledTask-list-card-view">
          {data.items.length === 0 ? (
            <EmptyMessage message="No Scheduled Task Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
    </div>
  );
};
